import React, { useEffect } from "react";
import styled from "styled-components";
import { Field, FieldProps } from "formik";
import * as Yup from "yup";
import {
  Image,
  Icon,
  IconName,
  Theme,
  colors,
  media,
} from "@literati/public-ui-shared-react";
import { ImageProps } from "@literati/public-ui-shared-react";
import { CheckoutSession } from "lib/api/checkout";
import { SubscriptionPlanCamelCase } from "lib/api/subscription";
import StepLayout, { StepLayoutProps } from "components/Step/StepLayout";
import { BudgetValues, FormikSubmitStep } from "components/Step/stepTypes";
import {
  Plans,
  SelectPlanKey as Key,
  SelectPlanValues,
} from "components/PlanSelection/planSelectionTypes";
import PlanSelectionRadio from "components/PlanSelection/PlanSelectionRadio";
import { useSelectPlan } from "components/PlanSelection/useSelectPlan";
import { FEATURE_FLAG_SHOW_FALL25_COUPON } from "components/Membership/membershipFeatureFlags";
import { isBudgetRangeGreaterThan } from "components/Step/stepUtil";
import MembershipFall25OfferBox from "components/Membership/MembershipFall25OfferBox";

export const VALIDATION_SCHEMA = Yup.object().shape({
  [Key.PLAN_SLUG]: Yup.string().required(),
});

const StyledDiv = styled.div`
  margin: 1rem 0 0;

  > * + * {
    margin-top: 1.5rem;
  }

  ${media.md} {
    margin: 2rem 0 0;
  }
`;

const MonthlyOfferLabel = styled.label<{ $checked: boolean }>`
  position: relative;
  display: block;
  border: 1px solid
    ${({ $checked }) =>
      $checked ? colors.periwinkleShade1 : colors.newsprint3};
  border-radius: 12px;
  box-shadow: ${({ $checked }) =>
    $checked ? "0 2px 10px rgb(0 0 0 / 20%)" : "none"};
  cursor: pointer;
`;

const MonthlyInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const Checkmark = styled.div<{ $checked: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 1px solid
    ${({ $checked }) => ($checked ? "transparent" : colors.newsprint3)};
  background: ${({ $checked }) =>
    $checked ? colors.periwinkleShade1 : colors.transparent};
  z-index: 1;
`;

const CheckmarkIcon = styled(Icon)`
  width: 1rem;
  height: 1rem;
`;

const OfferImage = styled(Image)`
  display: block;
  margin: 0 0 0.5rem;
`;

export type StepLayoutPropsSelectPlan = Omit<
  StepLayoutProps<SelectPlanValues>,
  "asForm" | "children" | "formId"
>;

export interface StepSelectPlanProps extends StepLayoutPropsSelectPlan {
  budgetValues?: Partial<BudgetValues> | null;
  checkoutSession?: CheckoutSession | null;
  /**
   * Fired when the in-form plan choice changes (including initial value after mount).
   * Use this before submit — `selectedPlan` from checkout state only updates after Continue.
   */
  onSelectedPlanSlugChange?: (planSlug: string) => void;
  onSubmit?: FormikSubmitStep<SelectPlanValues>;
  plans?: Plans | null;
  /** Set by membership flow after user leaves select-plan; controls default radio vs first-visit offer default. */
  selectPlanChoiceCommitted?: boolean;
  selectPlanImageProps?: ImageProps | null;
  selectedPlan?: SubscriptionPlanCamelCase | null;
}

function SelectedPlanSlugNotifier({
  planSlug,
  onSelectedPlanSlugChange,
}: {
  planSlug: string;
  onSelectedPlanSlugChange?: (planSlug: string) => void;
}) {
  useEffect(() => {
    onSelectedPlanSlugChange?.(planSlug);
  }, [planSlug, onSelectedPlanSlugChange]);

  return null;
}

export const StepSelectPlan = ({
  budgetValues,
  checkoutSession,
  onSubmit,
  onSelectedPlanSlugChange,
  plans,
  selectPlanChoiceCommitted,
  selectPlanImageProps,
  selectedPlan,
  ...stepLayoutProps
}: StepSelectPlanProps) => {
  const { handleSubmit } = useSelectPlan({ onSubmit });
  const showMonthlyOfferRadio =
    FEATURE_FLAG_SHOW_FALL25_COUPON &&
    isBudgetRangeGreaterThan(budgetValues, 15);

  const annualPlan = plans?.annual;
  const monthlyPlan = plans?.monthly2025;
  // Slug from React state or checkout session after a prior submit (back navigation).
  const persistedPlanSlug =
    selectedPlan?.slug || checkoutSession?.selectedPlan || "";
  // First visit with Fall25 dual radios: default to the monthly offer. After they’ve continued once,
  // prefer persisted slug so annual (or monthly) survives back from checkout/payment.
  const defaultPlanSlug =
    showMonthlyOfferRadio && !selectPlanChoiceCommitted
      ? monthlyPlan?.slug || persistedPlanSlug || annualPlan?.slug || ""
      : persistedPlanSlug ||
        (showMonthlyOfferRadio ? monthlyPlan?.slug : undefined) ||
        annualPlan?.slug ||
        "";

  return (
    <Theme>
      <StepLayout<SelectPlanValues>
        asForm
        bgColor="white"
        disableContentSpacing
        formId="step-select-plan"
        formikProps={{
          enableReinitialize: true,
          initialValues: {
            [Key.KIND]: "select-plan",
            [Key.PLAN_SLUG]: defaultPlanSlug,
          },
          onSubmit: handleSubmit,
          validationSchema: VALIDATION_SCHEMA,
        }}
        heading="Choose your plan option:"
        {...stepLayoutProps}
      >
        {({ values }) => {
          return (
            <>
              <SelectedPlanSlugNotifier
                onSelectedPlanSlugChange={onSelectedPlanSlugChange}
                planSlug={values[Key.PLAN_SLUG]}
              />
              <StyledDiv
                role="group"
                aria-labelledby="plan selection radio group"
              >
                {showMonthlyOfferRadio && monthlyPlan ? (
                  <Field name={Key.PLAN_SLUG}>
                    {({ field }: FieldProps) => {
                      const checked =
                        values[Key.PLAN_SLUG] === monthlyPlan.slug;
                      return (
                        <MonthlyOfferLabel
                          $checked={checked}
                          data-testid="radio-option-monthly"
                        >
                          <MonthlyInput
                            {...field}
                            id="monthly"
                            type="radio"
                            value={monthlyPlan.slug}
                          />
                          <Checkmark $checked={checked}>
                            {checked ? (
                              <CheckmarkIcon
                                fill="white"
                                name={IconName.CHECKMARK}
                              />
                            ) : null}
                          </Checkmark>
                          <MembershipFall25OfferBox
                            withCardChrome={false}
                            marginTop="0"
                            topImage={
                              selectPlanImageProps?.src ? (
                                <OfferImage
                                  alt="book box"
                                  {...selectPlanImageProps}
                                  contain
                                  targetWidth={173}
                                />
                              ) : undefined
                            }
                          />
                        </MonthlyOfferLabel>
                      );
                    }}
                  </Field>
                ) : null}
                {annualPlan ? (
                  <Field
                    checkoutSession={checkoutSession}
                    component={PlanSelectionRadio}
                    name={Key.PLAN_SLUG}
                    checked={values[Key.PLAN_SLUG] === annualPlan.slug}
                    imageProps={{ ...selectPlanImageProps, targetWidth: 173 }}
                    inputValue={annualPlan.slug}
                    monthlyPrice={annualPlan.costPerBox}
                    planName="Yearly Membership"
                    plans={plans}
                    variant="annual"
                    testId="radio-option-annual"
                  />
                ) : null}
                {monthlyPlan ? (
                  showMonthlyOfferRadio ? null : (
                    <Field
                      component={PlanSelectionRadio}
                      name={Key.PLAN_SLUG}
                      checked={values[Key.PLAN_SLUG] === monthlyPlan.slug}
                      inputValue={monthlyPlan.slug}
                      monthlyPrice={monthlyPlan.costPerBox}
                      planName="Monthly Membership"
                      testId="radio-option-monthly"
                      variant="monthly"
                    />
                  )
                ) : null}
              </StyledDiv>
            </>
          );
        }}
      </StepLayout>
    </Theme>
  );
};

export default StepSelectPlan;
